import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Theme = 'light' | 'dark';
const KEY = 'stable.theme';

const Ctx = createContext<{ theme: Theme; toggle(): void } | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => (document.documentElement.classList.contains('dark') ? 'dark' : 'light'));
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);
  return <Ctx.Provider value={{ theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme outside provider');
  return v;
}

/** Apply the stable's brand colour as --primary (hex → HSL), keeping contrast in dark mode. */
export function applyBrandColor(hex: string | null | undefined): void {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h /= 6;
  }
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  const root = document.documentElement.style;
  root.setProperty('--brand-light', `${H} ${S}% ${Math.min(L, 45)}%`);
  root.setProperty('--brand-dark', `${H} ${Math.max(S, 45)}% ${Math.max(L, 60)}%`);
  const style = document.getElementById('brand-style') ?? Object.assign(document.createElement('style'), { id: 'brand-style' });
  style.textContent = `:root{--primary:var(--brand-light);--ring:var(--brand-light)}.dark{--primary:var(--brand-dark);--ring:var(--brand-dark)}`;
  document.head.appendChild(style);
}
